

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import {
  FaBriefcase,
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaEdit,
  FaEnvelope,
  FaEye,
  FaFilePdf,
  FaGlobe,
  FaImage,
  FaMapMarkerAlt,
  FaPhone,
  FaPlus,
  FaSave,
  FaStar,
  FaTimes,
  FaTrash,
  FaUpload,
  FaUserEdit,
} from "react-icons/fa";

import PartnerSidebar from "../PartnerSidebar";
import "./AboutPartner.css";
import "./PartnerOffering.css";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const EMPTY_PROFILE = {
  agencyName: "",
  tagline: "",
  description: "",
  logo: "",
  email: "",
  alternateEmail: "",
  phone: "",
  alternatePhone: "",
  location: "",
  website: "",
  businessType: "",
  foundedYear: "",
  experience: "",
  teamSize: "",
  serviceArea: "",
  travelAvailability: "",
  responseTime: "",
  minimumBooking: "",
  languages: "",
  bookingNotice: "",
  workingHours: "",
  gstNumber: "",
  registrationNumber: "",
  specializations: "",
  eventTypes: "",
  targetAudience: "",
  portfolioLink: "",
  catalogueSummary: "",
  paymentTerms: "",
  cancellationPolicy: "",
  instagram: "",
  facebook: "",
  youtube: "",
  linkedin: "",
  twitter: "",
};

const emptyOffering = {
  offeringKind: "Service",
  name: "",
  offeringType: "Performance",
  code: "",
  description: "",
  category: "",
  artSector: "",
  language: "",
  region: "",
  eventTypes: "",
  price: "",
  discountPrice: "",
  unitType: "Event",
  stockQuantity: "",
  lowStockLimit: 5,
  duration: "",
  numberOfArtists: "",
  technicalRequirements: "",
  specifications: [{ key: "", value: "" }],
  mainImage: "",
  galleryImages: [],
  videoLink: "",
  status: "Active",
  travelAvailability: "",
  preparationTime: "",
  cancellationPolicy: "",
};

const offeringCategoryOptions = [
  "Live Performance",
  "Workshop",
  "Merchandise",
  "Digital Product",
  "Booking Service",
  "Ticketed Event",
  "Consultation",
  "Training",
  "Other",
];

const offeringArtSectorOptions = [
  "Music",
  "Dance",
  "Theatre",
  "Visual Arts",
  "Comedy",
  "Literature",
  "Craft",
  "Mixed Arts",
  "Other",
];

const offeringLanguageOptions = [
  "English",
  "Hindi",
  "Marathi",
  "Gujarati",
  "Tamil",
  "Telugu",
  "Bengali",
  "Punjabi",
  "Multilingual",
  "Not Applicable",
];

const offeringRegionOptions = [
  "All India",
  "Maharashtra",
  "Delhi NCR",
  "Karnataka",
  "Gujarat",
  "Rajasthan",
  "West Bengal",
  "Tamil Nadu",
  "Other",
];

const normalizeOfferingFromApi = (item = {}) => ({
  ...emptyOffering,
  ...item,
  id: item._id || item.id,
  offeringKind: item.offeringKind || "Service",
  name: item.name || "",
  offeringType: item.offeringType || "",
  code: item.code || "",
  discountPrice: item.discountPrice ?? "",
  stockQuantity:
    item.offeringKind === "Product" ? item.stockQuantity ?? "" : "",
  lowStockLimit:
    item.offeringKind === "Product" ? item.lowStockLimit ?? 5 : 0,
  numberOfArtists:
    item.offeringKind === "Service" && item.numberOfArtists
      ? item.numberOfArtists
      : "",
  specifications:
    Array.isArray(item.specifications) && item.specifications.length
      ? item.specifications
      : [{ key: "", value: "" }],
  galleryImages: Array.isArray(item.galleryImages) ? item.galleryImages : [],
});

const PartnerProfile = () => {
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [draftProfile, setDraftProfile] = useState({ ...EMPTY_PROFILE });
  const [isEditing, setIsEditing] = useState(false);
  const [profileLogoFile, setProfileLogoFile] = useState(null);
  const [isProfileSaving, setIsProfileSaving] = useState(false);

  const [offerings, setOfferings] = useState([]);
  const [isOfferingModalOpen, setIsOfferingModalOpen] = useState(false);
  const [editingOfferingId, setEditingOfferingId] = useState(null);
  const [offeringForm, setOfferingForm] = useState({ ...emptyOffering });
  const [offeringMainImageFile, setOfferingMainImageFile] = useState(null);
  const [offeringGalleryImageFiles, setOfferingGalleryImageFiles] = useState([]);
  const [isOfferingSaving, setIsOfferingSaving] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Full-detail "View" modal for a single offering (read-only).
  const [viewingOffering, setViewingOffering] = useState(null);

  const productCategories = useMemo(
    () =>
      [...new Set(offerings.map((item) => item.category).filter(Boolean))].slice(
        0,
        6
      ),
    [offerings]
  );

  // Reviews are not connected to an API in the supplied backend yet.
  // Keep this empty rather than showing demo/fake customer reviews.
  const reviews = [];

  useEffect(() => {
    let mounted = true;

    const loadPartnerData = async () => {
      setIsPageLoading(true);

      try {
        const [profileResponse, offeringsResponse] = await Promise.all([
          apiClient.get("partner/profile"),
          apiClient.get("partner/products", { params: { page: 1, limit: 100 } }),
        ]);

        if (!mounted) return;

        const profileData = profileResponse.data?.data || {};
        const productData = offeringsResponse.data?.data || {};
        const productList = Array.isArray(productData.products)
          ? productData.products
          : [];

        const normalizedProfile = {
          ...EMPTY_PROFILE,
          ...profileData,
        };

        setProfile(normalizedProfile);
        setDraftProfile(normalizedProfile);
        setOfferings(productList.map(normalizeOfferingFromApi));
      } catch (error) {
        if (!mounted) return;

        toast.error(
          error.response?.data?.message ||
            "Unable to load partner profile and products/services",
          { position: "top-center" }
        );
      } finally {
        if (mounted) setIsPageLoading(false);
      }
    };

    loadPartnerData();

    return () => {
      mounted = false;
    };
  }, []);

  const completionFields = [
    { key: "agencyName", label: "Agency name" },
    { key: "logo", label: "Agency logo" },
    { key: "tagline", label: "Tagline" },
    { key: "description", label: "About agency" },
    { key: "location", label: "Location" },
    { key: "website", label: "Website" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "businessType", label: "Business type" },
    { key: "foundedYear", label: "Founded year" },
    { key: "experience", label: "Experience" },
    { key: "teamSize", label: "Team size" },
    { key: "serviceArea", label: "Service area" },
    { key: "travelAvailability", label: "Travel availability" },
    { key: "languages", label: "Languages" },
    { key: "workingHours", label: "Working hours" },
    { key: "specializations", label: "Specializations" },
    { key: "eventTypes", label: "Event types" },
    { key: "targetAudience", label: "Target audience" },
    { key: "catalogueSummary", label: "Products and services summary" },
    { key: "portfolioLink", label: "Portfolio link" },
  ];

  const profileCompletion = useMemo(() => {
    const completed = completionFields.filter(({ key }) => {
      const value = profile[key];
      return typeof value === "string" && value.trim().length > 0;
    }).length;

    return Math.round((completed / completionFields.length) * 100);
  }, [profile]);

  const missingDetails = useMemo(
    () =>
      completionFields
        .filter(({ key }) => {
          const value = profile[key];
          return !value || !value.trim();
        })
        .map(({ label }) => label),
    [profile]
  );

  const averageRating = useMemo(() => {
    if (!reviews.length) return "0.0";
    const total = reviews.reduce((sum, review) => sum + review.rating, 0);
    return (total / reviews.length).toFixed(1);
  }, [reviews]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setDraftProfile((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setProfileLogoFile(file);
    setDraftProfile((current) => ({
      ...current,
      logo: URL.createObjectURL(file),
    }));
  };

  const handleEdit = () => {
    setDraftProfile({ ...EMPTY_PROFILE, ...profile });
    setProfileLogoFile(null);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftProfile({ ...EMPTY_PROFILE, ...profile });
    setProfileLogoFile(null);
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!draftProfile.agencyName.trim()) {
      toast.error("Agency name is required", { position: "top-center" });
      return;
    }

    setIsProfileSaving(true);

    try {
      const formData = new FormData();

      Object.keys(EMPTY_PROFILE).forEach((field) => {
        if (field === "logo") return;
        formData.append(field, draftProfile[field] ?? "");
      });

      if (profileLogoFile) {
        formData.append("logo", profileLogoFile);
      }

      const response = await apiClient.patch("partner/profile", formData);
      const savedProfile = {
        ...EMPTY_PROFILE,
        ...(response.data?.data || {}),
      };

      setProfile(savedProfile);
      setDraftProfile(savedProfile);
      setProfileLogoFile(null);
      setIsEditing(false);

      toast.success("Profile updated successfully", {
        position: "top-center",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Unable to update partner profile",
        { position: "top-center" }
      );
    } finally {
      setIsProfileSaving(false);
    }
  };

  const openAddOfferingForm = () => {
    setEditingOfferingId(null);
    setOfferingMainImageFile(null);
    setOfferingGalleryImageFiles([]);
    setOfferingForm({
      ...emptyOffering,
      specifications: [{ key: "", value: "" }],
      galleryImages: [],
    });
    setIsOfferingModalOpen(true);
  };

  const openEditOfferingForm = (offering) => {
    setEditingOfferingId(offering.id || offering._id);
    setOfferingMainImageFile(null);
    setOfferingGalleryImageFiles([]);
    setOfferingForm({
      ...emptyOffering,
      ...offering,
      id: offering.id || offering._id,
      offeringKind: offering.offeringKind || "Service",
      offeringType: offering.offeringType || "",
      code: offering.code || "",
      price: offering.price ?? "",
      discountPrice: offering.discountPrice ?? "",
      stockQuantity:
        offering.offeringKind === "Product"
          ? offering.stockQuantity ?? ""
          : "",
      lowStockLimit:
        offering.offeringKind === "Product"
          ? offering.lowStockLimit ?? 5
          : 0,
      numberOfArtists:
        offering.offeringKind === "Service" && offering.numberOfArtists
          ? offering.numberOfArtists
          : "",
      specifications:
        offering.specifications?.length > 0
          ? offering.specifications
          : [{ key: "", value: "" }],
      galleryImages: offering.galleryImages || [],
    });
    setIsOfferingModalOpen(true);
  };

  const closeOfferingForm = () => {
    setIsOfferingModalOpen(false);
    setEditingOfferingId(null);
    setOfferingMainImageFile(null);
    setOfferingGalleryImageFiles([]);
    setOfferingForm({ ...emptyOffering });
  };

  const handleOfferingFieldChange = (event) => {
    const { name, value } = event.target;

    setOfferingForm((current) => {
      if (name === "offeringKind") {
        const isProduct = value === "Product";
        return {
          ...current,
          offeringKind: value,
          offeringType: isProduct ? "Physical Product" : "Performance",
          unitType: isProduct ? "Piece" : "Event",
          stockQuantity: isProduct ? current.stockQuantity : "",
          lowStockLimit: isProduct ? current.lowStockLimit || 5 : 0,
          duration: isProduct ? "" : current.duration,
          numberOfArtists: isProduct ? "" : current.numberOfArtists,
          technicalRequirements: isProduct
            ? ""
            : current.technicalRequirements,
        };
      }

      return {
        ...current,
        [name]: value,
      };
    });
  };

  const handleOfferingMainImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      toast.error("Please select an image smaller than 1 MB", {
        position: "top-center",
      });
      return;
    }

    setOfferingMainImageFile(file);
    setOfferingForm((current) => ({
      ...current,
      mainImage: URL.createObjectURL(file),
    }));
  };

  const handleOfferingGalleryImages = (event) => {
    const files = Array.from(event.target.files || []).slice(0, 4);
    if (!files.length) return;

    if (files.some((file) => file.size > 1024 * 1024)) {
      toast.error("Each gallery image must be smaller than 1 MB", {
        position: "top-center",
      });
      return;
    }

    setOfferingGalleryImageFiles(files);
    setOfferingForm((current) => ({
      ...current,
      galleryImages: files.map((file) => URL.createObjectURL(file)),
    }));
  };

  const handleOfferingSpecificationChange = (index, field, value) => {
    setOfferingForm((current) => ({
      ...current,
      specifications: current.specifications.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addOfferingSpecification = () => {
    setOfferingForm((current) => ({
      ...current,
      specifications: [...current.specifications, { key: "", value: "" }],
    }));
  };

  const removeOfferingSpecification = (index) => {
    setOfferingForm((current) => ({
      ...current,
      specifications:
        current.specifications.length === 1
          ? [{ key: "", value: "" }]
          : current.specifications.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  const handleOfferingSubmit = async (event) => {
    event.preventDefault();

    const requiredFields = [
      offeringForm.offeringKind,
      offeringForm.name,
      offeringForm.offeringType,
      offeringForm.code,
      offeringForm.description,
      offeringForm.category,
      offeringForm.artSector,
      offeringForm.language,
      offeringForm.region,
    ];

    if (
      requiredFields.some((value) => !String(value || "").trim()) ||
      offeringForm.price === "" ||
      Number(offeringForm.price) < 0
    ) {
      toast.error("Please complete all required offering fields", {
        position: "top-center",
      });
      return;
    }

    if (
      offeringForm.discountPrice !== "" &&
      Number(offeringForm.discountPrice) > Number(offeringForm.price)
    ) {
      toast.error("Discount price cannot be greater than the regular price", {
        position: "top-center",
      });
      return;
    }

    setIsOfferingSaving(true);

    try {
      const formData = new FormData();
      const cleanedSpecifications = offeringForm.specifications.filter(
        (item) => item.key.trim() || item.value.trim()
      );

      const fields = [
        "offeringKind",
        "name",
        "offeringType",
        "code",
        "description",
        "category",
        "artSector",
        "language",
        "region",
        "eventTypes",
        "price",
        "discountPrice",
        "unitType",
        "travelAvailability",
        "preparationTime",
        "cancellationPolicy",
        "videoLink",
        "status",
      ];

      fields.forEach((field) => {
        formData.append(field, offeringForm[field] ?? "");
      });

      if (offeringForm.offeringKind === "Product") {
        formData.append("stockQuantity", offeringForm.stockQuantity || 0);
        formData.append("lowStockLimit", offeringForm.lowStockLimit || 0);
      } else {
        formData.append("duration", offeringForm.duration || "");
        formData.append(
          "numberOfArtists",
          offeringForm.numberOfArtists === ""
            ? 0
            : offeringForm.numberOfArtists
        );
        formData.append(
          "technicalRequirements",
          offeringForm.technicalRequirements || ""
        );
      }

      formData.append(
        "specifications",
        JSON.stringify(cleanedSpecifications)
      );

      if (offeringMainImageFile) {
        formData.append("mainImage", offeringMainImageFile);
      }

      offeringGalleryImageFiles.forEach((file) => {
        formData.append("galleryImages", file);
      });

      const response = editingOfferingId
        ? await apiClient.patch(`partner/products/${editingOfferingId}`, formData)
        : await apiClient.post("partner/products", formData);

      const savedOffering = normalizeOfferingFromApi(
        response.data?.data || {}
      );

      setOfferings((current) =>
        editingOfferingId
          ? current.map((item) =>
              item.id === editingOfferingId ? savedOffering : item
            )
          : [savedOffering, ...current]
      );

      toast.success(
        editingOfferingId
          ? "Product / Service updated successfully"
          : `${offeringForm.offeringKind} added successfully`,
        { position: "top-center" }
      );

      closeOfferingForm();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Unable to save product / service",
        { position: "top-center" }
      );
    } finally {
      setIsOfferingSaving(false);
    }
  };

  const handleDeleteOffering = async (offering) => {
    const id = offering.id || offering._id;
    const shouldDelete = window.confirm(`Delete "${offering.name}"?`);
    if (!shouldDelete) return;

    try {
      await apiClient.delete(`partner/products/${id}`);
      setOfferings((current) => current.filter((item) => item.id !== id));

      toast.success("Product / Service deleted successfully", {
        position: "top-center",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Unable to delete product / service",
        { position: "top-center" }
      );
    }
  };

  const getOfferingKind = (offering) =>
    offering.offeringKind ||
    (String(offering.type || "").toLowerCase().includes("product") ||
    offering.category === "Merchandise"
      ? "Product"
      : "Service");

  const getOfferingPrice = (offering) => {
    const regularPrice = Number(
      offering.price ??
        (String(offering.startingPrice || "").replace(/[^0-9.]/g, "") || 0)
    );
    const discountPrice = Number(offering.discountPrice || 0);
    return discountPrice > 0 && discountPrice < regularPrice
      ? discountPrice
      : regularPrice;
  };

  const getOfferingAvailability = (offering) => {
    if (offering.status && offering.status !== "Active") {
      return offering.status;
    }

    if (getOfferingKind(offering) === "Product") {
      const stock = Number(offering.stockQuantity || 0);
      const lowStockLimit = Number(offering.lowStockLimit || 0);
      if (stock <= 0) return "Out of Stock";
      if (stock <= lowStockLimit) return "Limited";
      return "Available";
    }

    return "Available";
  };

  const renderStars = (rating) =>
    Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={index < rating ? "star-filled" : "star-empty"}
      />
    ));

  // ---------------------------------------------------------------------
  // Business Brochure (PDF) generator
  // ---------------------------------------------------------------------
  const buildBrochureDefaults = (source) => ({
    agencyName: source.agencyName || "",
    tagline: source.tagline || "",
    description: source.description || "",
    businessType: source.businessType || "",
    foundedYear: source.foundedYear || "",
    experience: source.experience || "",
    teamSize: source.teamSize || "",
    serviceArea: source.serviceArea || "",
    travelAvailability: source.travelAvailability || "",
    languages: source.languages || "",
    workingHours: source.workingHours || "",
    specializations: source.specializations || "",
    eventTypes: source.eventTypes || "",
    targetAudience: source.targetAudience || "",
    catalogueSummary: source.catalogueSummary || "",
    location: source.location || "",
    website: source.website || "",
  });

  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);
  const [brochureDetails, setBrochureDetails] = useState(() =>
    buildBrochureDefaults(EMPTY_PROFILE)
  );
  const [brochureLogo, setBrochureLogo] = useState(EMPTY_PROFILE.logo || "");
  const [brochureProducts, setBrochureProducts] = useState([]);
  const [productDraft, setProductDraft] = useState({
    name: "",
    price: "",
    description: "",
    image: "",
  });
  const [isGeneratingBrochure, setIsGeneratingBrochure] = useState(false);

  const openBrochureForm = () => {
    setBrochureDetails(buildBrochureDefaults(profile));
    setBrochureLogo(profile.logo || "");
    // Pre-fill with existing offerings so partners can add photos/edit
    // rather than starting from a blank list.
    setBrochureProducts(
      offerings.map((offering) => ({
        id: `offering-${offering.id}`,
        name: offering.name,
        price:
          offering.price !== "" && offering.price !== undefined
            ? `₹${Number(offering.price).toLocaleString("en-IN")}`
            : "",
        description: [offering.category, offering.offeringType]
          .filter(Boolean)
          .join(" • "),
        image: offering.mainImage || "",
      }))
    );
    setProductDraft({ name: "", price: "", description: "", image: "" });
    setIsBrochureModalOpen(true);
  };

  const closeBrochureForm = () => setIsBrochureModalOpen(false);

  const handleBrochureFieldChange = (event) => {
    const { name, value } = event.target;
    setBrochureDetails((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleBrochureLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBrochureLogo(URL.createObjectURL(file));
  };

  const handleProductDraftChange = (event) => {
    const { name, value } = event.target;
    setProductDraft((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleProductDraftImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProductDraft((current) => ({
      ...current,
      image: URL.createObjectURL(file),
    }));
  };

  const handleAddBrochureProduct = () => {
    if (!productDraft.name.trim()) {
      toast.error("Add a product or service name first", {
        position: "top-center",
      });
      return;
    }

    setBrochureProducts((current) => [
      ...current,
      { ...productDraft, id: `product-${Date.now()}` },
    ]);
    setProductDraft({ name: "", price: "", description: "", image: "" });
  };

  const handleRemoveBrochureProduct = (id) => {
    setBrochureProducts((current) => current.filter((item) => item.id !== id));
  };

  // Loads an image URL (object URL or remote URL) into a canvas so it can be
  // embedded in the PDF as a data URL. Resolves to null if the image can't
  // be loaded, so brochure generation never blocks on a missing/broken logo.
  const loadImageForPdf = (url) =>
    new Promise((resolve) => {
      if (!url) {
        resolve(null);
        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve({
            dataUrl: canvas.toDataURL("image/png"),
            ratio: img.naturalWidth / img.naturalHeight || 1,
          });
        } catch (err) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = url;
    });

  const handleGenerateBrochure = async (event) => {
    event.preventDefault();
    setIsGeneratingBrochure(true);

    try {
      // Brand palette used throughout the brochure — matches the site's
      // maroon theme (--profile-maroon / --profile-maroon-dark / --profile-amber)
      const primaryColor = [173, 47, 59]; // --profile-maroon
      const primaryDark = [127, 31, 43]; // --profile-maroon-dark
      const accentColor = [168, 109, 0]; // --profile-amber
      const darkText = [23, 23, 23]; // --profile-black
      const mutedText = [103, 103, 103]; // --profile-grey
      const lightBg = [248, 233, 236]; // --profile-maroon-light
      const borderColor = [221, 221, 221]; // --profile-border

      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 48;
      const contentWidth = pageWidth - margin * 2;
      const footerHeight = 44;
      let cursorY = 0;
      let pageNumber = 1;

      const generatedDate = new Date().toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });

      const logoData = await loadImageForPdf(brochureLogo);
      const productImageData = await Promise.all(
        brochureProducts.map((product) => loadImageForPdf(product.image))
      );

      const drawFooter = () => {
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.75);
        doc.line(margin, pageHeight - footerHeight, pageWidth - margin, pageHeight - footerHeight);

        doc.setFillColor(...primaryColor);
        doc.rect(margin, pageHeight - footerHeight + 14, 6, 6, "F");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...mutedText);

        const footerLeft = [brochureDetails.website, brochureDetails.location]
          .filter(Boolean)
          .join("   |   ");
        if (footerLeft) {
          doc.text(footerLeft, margin + 14, pageHeight - footerHeight + 20);
        }

        doc.text(
          `Page ${pageNumber}  •  Generated ${generatedDate}`,
          pageWidth - margin,
          pageHeight - footerHeight + 20,
          { align: "right" }
        );
      };

      const startNewPage = () => {
        drawFooter();
        doc.addPage();
        pageNumber += 1;
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, pageWidth, 8, "F");
        cursorY = 46;
      };

      const ensureSpace = (needed) => {
        if (cursorY + needed > pageHeight - footerHeight - 16) {
          startNewPage();
        }
      };

      // ---------------- Header band ----------------
      const headerHeight = 150;
      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, headerHeight, "F");
      // a single darker maroon wedge on the right for a subtle gradient feel,
      // echoing the site's maroon-to-maroon-dark header treatment
      doc.setFillColor(...primaryDark);
      doc.triangle(
        pageWidth * 0.62,
        0,
        pageWidth,
        0,
        pageWidth,
        headerHeight,
        "F"
      );
      doc.setFillColor(...accentColor);
      doc.rect(0, headerHeight, pageWidth, 5, "F");

      let textStartX = margin;

      if (logoData?.dataUrl) {
        const logoBoxSize = 78;
        const logoBoxX = margin;
        const logoBoxY = (headerHeight - logoBoxSize) / 2;

        doc.setFillColor(255, 255, 255);
        doc.roundedRect(logoBoxX, logoBoxY, logoBoxSize, logoBoxSize, 8, 8, "F");

        const pad = 8;
        const maxDim = logoBoxSize - pad * 2;
        let drawW = maxDim;
        let drawH = maxDim / logoData.ratio;
        if (drawH > maxDim) {
          drawH = maxDim;
          drawW = maxDim * logoData.ratio;
        }
        const imgX = logoBoxX + (logoBoxSize - drawW) / 2;
        const imgY = logoBoxY + (logoBoxSize - drawH) / 2;
        doc.addImage(logoData.dataUrl, "PNG", imgX, imgY, drawW, drawH);

        textStartX = logoBoxX + logoBoxSize + 20;
      }

      const nameMaxWidth = pageWidth - textStartX - margin;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      const nameLines = doc.splitTextToSize(
        brochureDetails.agencyName || "Business Brochure",
        nameMaxWidth
      );

      let nameY = headerHeight / 2 - (brochureDetails.tagline ? 16 : 4);
      doc.setTextColor(255, 255, 255);
      doc.text(nameLines, textStartX, nameY);

      if (brochureDetails.tagline) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(11.5);
        doc.setTextColor(245, 214, 219);
        const tagY = nameY + nameLines.length * 26 + 4;
        const tagLines = doc.splitTextToSize(brochureDetails.tagline, nameMaxWidth);
        doc.text(tagLines, textStartX, tagY);
      }

      cursorY = headerHeight + 36;

      // ---------------- Category chips ----------------
      const chipSource = [
        brochureDetails.businessType,
        ...(brochureDetails.specializations || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      ]
        .filter(Boolean)
        .slice(0, 5);

      if (chipSource.length) {
        const chipHeight = 22;
        ensureSpace(chipHeight + 20);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);

        let chipX = margin;
        chipSource.forEach((label) => {
          const text = label.toUpperCase();
          const chipWidth = doc.getTextWidth(text) + 20;

          if (chipX + chipWidth > pageWidth - margin) {
            chipX = margin;
            cursorY += chipHeight + 8;
          }

          doc.setFillColor(...lightBg);
          doc.setDrawColor(224, 189, 195);
          doc.setLineWidth(0.75);
          doc.roundedRect(chipX, cursorY, chipWidth, chipHeight, 11, 11, "FD");

          doc.setTextColor(...primaryDark);
          doc.text(text, chipX + 10, cursorY + 14.5);

          chipX += chipWidth + 8;
        });

        cursorY += chipHeight + 26;
      }

      // ---------------- Section helpers ----------------
      const sectionHeading = (title, x = margin, width = contentWidth) => {
        ensureSpace(34);
        doc.setFillColor(...accentColor);
        doc.rect(x, cursorY - 11, 4, 14, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...darkText);
        doc.text(title, x + 12, cursorY);
        cursorY += 10;
        doc.setDrawColor(...borderColor);
        doc.setLineWidth(0.75);
        doc.line(x, cursorY, x + width, cursorY);
        cursorY += 18;
      };

      const bodyParagraph = (text, size = 10.5) => {
        if (!text) return;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(size);
        doc.setTextColor(...darkText);
        const lines = doc.splitTextToSize(text, contentWidth);
        ensureSpace(lines.length * (size + 4) + 6);
        doc.text(lines, margin, cursorY);
        cursorY += lines.length * (size + 4) + 14;
      };

      const highlightBlock = (title, value) => {
        if (!value) return;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        const lines = doc.splitTextToSize(value, contentWidth - 16);
        const blockHeight = 16 + lines.length * 14 + 8;
        ensureSpace(blockHeight);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5);
        doc.setTextColor(...primaryColor);
        doc.text(title, margin + 14, cursorY);
        cursorY += 14;

        doc.setFillColor(...accentColor);
        doc.rect(margin, cursorY - lines.length * 14 - 2, 3, lines.length * 14 + 6, "F");

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        doc.setTextColor(...darkText);
        doc.text(lines, margin + 14, cursorY);
        cursorY += lines.length * 14 + 16;
      };

      // ---------------- Snapshot sidebar + About (two-column intro) ----------------
      const quickFacts = [
        ["Business Type", brochureDetails.businessType],
        ["Founded", brochureDetails.foundedYear],
        ["Experience", brochureDetails.experience],
        ["Team Size", brochureDetails.teamSize],
        ["Service Area", brochureDetails.serviceArea],
        ["Travel Availability", brochureDetails.travelAvailability],
        ["Languages", brochureDetails.languages],
        ["Working Hours", brochureDetails.workingHours],
      ].filter(([, value]) => value);

      const sidebarWidth = 168;
      const columnGap = 22;
      const rightColumnX = margin + sidebarWidth + columnGap;
      const rightColumnWidth = contentWidth - sidebarWidth - columnGap;

      // Long descriptions or many facts don't fit two columns cleanly on
      // one page, so fall back to a full-width stacked layout for those.
      const useSideBySide =
        quickFacts.length > 0 &&
        quickFacts.length <= 8 &&
        (brochureDetails.description || "").length <= 480;

      const twoColTop = cursorY;
      let sidebarBottom = twoColTop;
      let aboutBottom = twoColTop;

      if (quickFacts.length && useSideBySide) {
        const sidebarPaddingX = 14;
        const sidebarPaddingTop = 26;
        const rowHeight = 33;
        const sidebarHeight = sidebarPaddingTop + quickFacts.length * rowHeight + 8;

        ensureSpace(sidebarHeight);

        doc.setFillColor(...lightBg);
        doc.roundedRect(margin, cursorY, sidebarWidth, sidebarHeight, 8, 8, "F");
        doc.setFillColor(...primaryColor);
        doc.roundedRect(margin, cursorY, sidebarWidth, 6, 3, 3, "F");
        doc.rect(margin, cursorY + 3, sidebarWidth, 3, "F");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9.5);
        doc.setTextColor(...primaryDark);
        doc.text("BUSINESS SNAPSHOT", margin + sidebarPaddingX, cursorY + 20);

        let factY = cursorY + sidebarPaddingTop + 6;
        quickFacts.forEach(([label, value], index) => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(7.5);
          doc.setTextColor(...mutedText);
          doc.text(label.toUpperCase(), margin + sidebarPaddingX, factY);

          doc.setFont("helvetica", "bold");
          doc.setFontSize(9.5);
          doc.setTextColor(...darkText);
          const valueLines = doc.splitTextToSize(value, sidebarWidth - sidebarPaddingX * 2);
          doc.text(valueLines.slice(0, 1), margin + sidebarPaddingX, factY + 12);

          if (index < quickFacts.length - 1) {
            doc.setDrawColor(224, 189, 195);
            doc.setLineWidth(0.5);
            doc.line(
              margin + sidebarPaddingX,
              factY + 20,
              margin + sidebarWidth - sidebarPaddingX,
              factY + 20
            );
          }

          factY += rowHeight;
        });

        sidebarBottom = cursorY + sidebarHeight;

        if (brochureDetails.description) {
          doc.setFillColor(...accentColor);
          doc.rect(rightColumnX, twoColTop + 2, 4, 14, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(13);
          doc.setTextColor(...darkText);
          doc.text("About the Business", rightColumnX + 12, twoColTop + 13);

          doc.setFont("helvetica", "normal");
          doc.setFontSize(10.5);
          doc.setTextColor(...darkText);
          const descLines = doc.splitTextToSize(brochureDetails.description, rightColumnWidth);
          const descY = twoColTop + 34;
          doc.text(descLines, rightColumnX, descY);
          aboutBottom = descY + descLines.length * 14.5;
        }

        cursorY = Math.max(sidebarBottom, aboutBottom) + 26;
      } else {
        if (brochureDetails.description) {
          sectionHeading("About the Business");
          bodyParagraph(brochureDetails.description);
        }

        if (quickFacts.length) {
          sectionHeading("Business Snapshot");

          const gap = 14;
          const colWidth = (contentWidth - gap) / 2;
          const rowHeight = 46;

          quickFacts.forEach(([label, value], index) => {
            const col = index % 2;
            if (col === 0) ensureSpace(rowHeight + 10);

            const boxX = margin + col * (colWidth + gap);
            const boxY = cursorY;

            doc.setFillColor(...lightBg);
            doc.setDrawColor(224, 189, 195);
            doc.setLineWidth(0.75);
            doc.roundedRect(boxX, boxY, colWidth, rowHeight, 6, 6, "FD");

            doc.setFont("helvetica", "normal");
            doc.setFontSize(8.5);
            doc.setTextColor(...mutedText);
            doc.text(label.toUpperCase(), boxX + 12, boxY + 16);

            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...darkText);
            const valueLines = doc.splitTextToSize(value, colWidth - 24);
            doc.text(valueLines.slice(0, 2), boxX + 12, boxY + 32);

            if (col === 1) cursorY += rowHeight + 10;
          });

          if (quickFacts.length % 2 === 1) cursorY += rowHeight + 10;
          cursorY += 6;
        }
      }

      // ---------------- Services & expertise ----------------
      const hasServiceContent =
        brochureDetails.specializations ||
        brochureDetails.eventTypes ||
        brochureDetails.targetAudience ||
        brochureDetails.catalogueSummary;

      if (hasServiceContent) {
        sectionHeading("Services & Expertise");
        highlightBlock("Specializations", brochureDetails.specializations);
        highlightBlock("Event Types Served", brochureDetails.eventTypes);
        highlightBlock("Target Customers", brochureDetails.targetAudience);
        highlightBlock("Products & Services", brochureDetails.catalogueSummary);
      }

      // ---------------- Product & service showcase ----------------
      if (brochureProducts.length) {
        sectionHeading("Product & Service Showcase");

        const cardPadding = 12;
        const imageBoxSize = 70;
        const textX = margin + imageBoxSize + cardPadding * 2;
        const textWidth = contentWidth - imageBoxSize - cardPadding * 3;

        brochureProducts.forEach((product, index) => {
          const imageData = productImageData[index];

          doc.setFont("helvetica", "normal");
          doc.setFontSize(9.5);
          const descLines = product.description
            ? doc.splitTextToSize(product.description, textWidth)
            : [];

          const cardHeight = Math.max(
            imageBoxSize + cardPadding * 2,
            cardPadding * 2 + 16 + descLines.length * 12
          );

          ensureSpace(cardHeight + 12);

          // Card background + border
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(...borderColor);
          doc.setLineWidth(0.75);
          doc.roundedRect(margin, cursorY, contentWidth, cardHeight, 8, 8, "FD");

          // Image tile
          const imgBoxX = margin + cardPadding;
          const imgBoxY = cursorY + cardPadding;
          doc.setFillColor(...lightBg);
          doc.roundedRect(imgBoxX, imgBoxY, imageBoxSize, imageBoxSize, 6, 6, "F");

          if (imageData?.dataUrl) {
            let drawW = imageBoxSize - 8;
            let drawH = drawW / imageData.ratio;
            if (drawH > imageBoxSize - 8) {
              drawH = imageBoxSize - 8;
              drawW = drawH * imageData.ratio;
            }
            const imgX = imgBoxX + (imageBoxSize - drawW) / 2;
            const imgY = imgBoxY + (imageBoxSize - drawH) / 2;
            doc.addImage(imageData.dataUrl, "PNG", imgX, imgY, drawW, drawH);
          } else {
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            doc.setTextColor(...primaryDark);
            doc.text(
              (product.name || "?").trim().charAt(0).toUpperCase(),
              imgBoxX + imageBoxSize / 2,
              imgBoxY + imageBoxSize / 2 + 3,
              { align: "center" }
            );
          }

          // Name + price
          let textY = cursorY + cardPadding + 12;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(11);
          doc.setTextColor(...darkText);

          const priceText = product.price ? String(product.price) : "";
          const priceWidth = priceText ? doc.getTextWidth(priceText) + 16 : 0;
          const nameMaxTextWidth = textWidth - (priceText ? priceWidth + 8 : 0);
          const nameLine = doc.splitTextToSize(product.name, nameMaxTextWidth)[0];
          doc.text(nameLine, textX, textY);

          if (priceText) {
            doc.setFillColor(...lightBg);
            doc.roundedRect(
              margin + contentWidth - priceWidth - cardPadding,
              cursorY + cardPadding,
              priceWidth,
              17,
              8,
              8,
              "F"
            );
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8.5);
            doc.setTextColor(...primaryDark);
            doc.text(
              priceText,
              margin + contentWidth - priceWidth - cardPadding + priceWidth / 2,
              cursorY + cardPadding + 11.5,
              { align: "center" }
            );
          }

          if (descLines.length) {
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9.5);
            doc.setTextColor(...mutedText);
            doc.text(descLines, textX, textY + 15);
          }

          cursorY += cardHeight + 12;
        });

        cursorY += 4;
      }

      // ---------------- Contact close ----------------
      if (brochureDetails.location || brochureDetails.website) {
        const contactLines = [
          brochureDetails.location ? `Location: ${brochureDetails.location}` : null,
          brochureDetails.website ? `Website: ${brochureDetails.website}` : null,
        ].filter(Boolean);

        const boxHeight = 34 + contactLines.length * 16;
        ensureSpace(boxHeight + 16);

        doc.setFillColor(...primaryDark);
        doc.roundedRect(margin, cursorY, contentWidth, boxHeight, 8, 8, "F");
        doc.setFillColor(...accentColor);
        doc.circle(margin + 26, cursorY + boxHeight / 2, 4, "F");

        doc.setTextColor(245, 214, 219);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);
        doc.text("GET IN TOUCH", margin + 44, cursorY + 20);

        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10.5);
        let ctY = cursorY + 20 + 16;
        contactLines.forEach((line) => {
          doc.text(line, margin + 44, ctY);
          ctY += 16;
        });

        cursorY += boxHeight + 16;
      }

      drawFooter();


      const safeName = (brochureDetails.agencyName || "business")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

      doc.save(`${safeName || "business"}-brochure.pdf`);

      toast.success("Business brochure PDF generated", {
        position: "top-center",
      });

      setIsBrochureModalOpen(false);
    } catch (error) {
      toast.error("Could not generate the brochure. Please try again.", {
        position: "top-center",
      });
    } finally {
      setIsGeneratingBrochure(false);
    }
  };

  return (
    <div className="partner-profile-layout">
      <PartnerSidebar />

      <main className="partner-profile-main">
        <div className="profile-page-container">
          <header className="profile-page-header">
            <div>
              <h1>Partner Profile</h1>
              <p>
                Manage agency information, business details, artist offerings,
                and platform visibility.
              </p>
            </div>

            {!isEditing && (
              <div className="profile-header-actions" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="profile-primary-button"
                  onClick={openAddOfferingForm}
                >
                  <FaPlus />
                  Add Product / Service
                </button>

                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={openBrochureForm}
                >
                  <FaFilePdf />
                  Generate Business Brochure
                </button>

                <button
                  type="button"
                  className="profile-primary-button"
                  onClick={handleEdit}
                >
                  <FaUserEdit />
                  Edit Profile
                </button>
              </div>
            )}
          </header>

          {isPageLoading && (
            <div className="profile-card" style={{ marginBottom: "16px" }}>
              Loading partner profile...
            </div>
          )}

          {isEditing ? (
            <form className="profile-edit-form" onSubmit={handleSave}>
              <section className="profile-form-card">
                <div className="profile-section-heading form-heading">
                  <div>
                    <h2>Edit Agency Profile</h2>
                    <p>
                      Add complete and accurate details to improve profile
                      visibility.
                    </p>
                  </div>

                  <div className="profile-form-actions">
                    <button
                      type="button"
                      className="profile-secondary-button"
                      onClick={handleCancel}
                    >
                      <FaTimes />
                      Cancel
                    </button>

                    <button type="submit" className="profile-primary-button" disabled={isProfileSaving}>
                      <FaSave />
                      {isProfileSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </div>

                <div className="profile-edit-logo-row">
                  <div className="profile-logo large">
                    {draftProfile.logo ? (
                      <img
                        src={draftProfile.logo}
                        alt="Agency logo preview"
                      />
                    ) : (
                      <FaBuilding />
                    )}
                  </div>

                  <div>
                    <label className="profile-upload-button">
                      <FaUpload />
                      Upload Agency Logo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                      />
                    </label>
                    <small>Recommended size: 500 × 500 px</small>
                  </div>
                </div>

                <FormSection title="Basic Information">
                  <ProfileField
                    label="Agency Name"
                    name="agencyName"
                    value={draftProfile.agencyName}
                    onChange={handleInputChange}
                    required
                  />
                  <ProfileField
                    label="Tagline"
                    name="tagline"
                    value={draftProfile.tagline}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Agency Description"
                    name="description"
                    value={draftProfile.description}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                    required
                  />
                </FormSection>

                <FormSection title="Contact Information">
                  <ProfileField
                    label="Email"
                    name="email"
                    type="email"
                    value={draftProfile.email}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Alternate Email"
                    name="alternateEmail"
                    type="email"
                    value={draftProfile.alternateEmail}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Phone"
                    name="phone"
                    value={draftProfile.phone}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Alternate Phone"
                    name="alternatePhone"
                    value={draftProfile.alternatePhone}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Location"
                    name="location"
                    value={draftProfile.location}
                    onChange={handleInputChange}
                    required
                  />
                  <ProfileField
                    label="Website"
                    name="website"
                    value={draftProfile.website}
                    onChange={handleInputChange}
                  />
                </FormSection>

                <FormSection title="Business Information">
                  <ProfileField
                    label="Business Type"
                    name="businessType"
                    value={draftProfile.businessType}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Founded Year"
                    name="foundedYear"
                    value={draftProfile.foundedYear}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Experience"
                    name="experience"
                    value={draftProfile.experience}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Team Size"
                    name="teamSize"
                    value={draftProfile.teamSize}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Service Area"
                    name="serviceArea"
                    value={draftProfile.serviceArea}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Travel Availability"
                    name="travelAvailability"
                    value={draftProfile.travelAvailability}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Response Time"
                    name="responseTime"
                    value={draftProfile.responseTime}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Working Hours"
                    name="workingHours"
                    value={draftProfile.workingHours}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Languages"
                    name="languages"
                    value={draftProfile.languages}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Minimum Booking"
                    name="minimumBooking"
                    value={draftProfile.minimumBooking}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Advance Booking Notice"
                    name="bookingNotice"
                    value={draftProfile.bookingNotice}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="GST Number"
                    name="gstNumber"
                    value={draftProfile.gstNumber}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Business Registration Number"
                    name="registrationNumber"
                    value={draftProfile.registrationNumber}
                    onChange={handleInputChange}
                  />
                </FormSection>

                <FormSection title="Services & Customer Information">
                  <ProfileField
                    label="Specializations"
                    name="specializations"
                    value={draftProfile.specializations}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                  <ProfileField
                    label="Event Types Served"
                    name="eventTypes"
                    value={draftProfile.eventTypes}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                  <ProfileField
                    label="Target Customers"
                    name="targetAudience"
                    value={draftProfile.targetAudience}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                  <ProfileField
                    label="Products & Services Summary"
                    name="catalogueSummary"
                    value={draftProfile.catalogueSummary}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                  <ProfileField
                    label="Portfolio Link"
                    name="portfolioLink"
                    value={draftProfile.portfolioLink}
                    onChange={handleInputChange}
                    fullWidth
                  />
                </FormSection>

                <FormSection title="Booking & Payment">
                  <ProfileField
                    label="Payment Terms"
                    name="paymentTerms"
                    value={draftProfile.paymentTerms}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                  <ProfileField
                    label="Cancellation Policy"
                    name="cancellationPolicy"
                    value={draftProfile.cancellationPolicy}
                    onChange={handleInputChange}
                    textarea
                    fullWidth
                  />
                </FormSection>

                <FormSection title="Social Media">
                  <ProfileField
                    label="Instagram"
                    name="instagram"
                    value={draftProfile.instagram}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Facebook"
                    name="facebook"
                    value={draftProfile.facebook}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="YouTube"
                    name="youtube"
                    value={draftProfile.youtube}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="LinkedIn"
                    name="linkedin"
                    value={draftProfile.linkedin}
                    onChange={handleInputChange}
                  />
                  <ProfileField
                    label="Twitter / X"
                    name="twitter"
                    value={draftProfile.twitter}
                    onChange={handleInputChange}
                  />
                </FormSection>
              </section>
            </form>
          ) : (
            <>
              <section className="profile-overview-grid">
                <article className="profile-hero-card profile-identity-card">
                  <div className="profile-brand-section">
                    <div className="profile-logo">
                      {profile.logo ? (
                        <img
                          src={profile.logo}
                          alt={`${profile.agencyName} logo`}
                        />
                      ) : (
                        <FaBuilding />
                      )}
                    </div>

                    <div className="profile-brand-content">
                      <div className="profile-name-row">
                        <h2>{profile.agencyName || "Partner Profile"}</h2>
                        <span className="verified-badge">
                          <FaCheckCircle />
                          Verified Partner
                        </span>
                      </div>

                      <p className="profile-tagline">{profile.tagline}</p>
                      <p className="profile-description">
                        {profile.description}
                      </p>

                      {productCategories.length > 0 && (
                        <div className="profile-category-tags">
                          {productCategories.map((category) => (
                            <span key={category}>{category}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>

                <aside className="profile-summary-column">
                  <article className="profile-completion-card">
                    <div className="completion-header">
                      <div>
                        <small>Profile Completion</small>
                        <strong>{profileCompletion}%</strong>
                      </div>
                      <span
                        className={
                          profileCompletion >= 85
                            ? "completion-status excellent"
                            : profileCompletion >= 60
                            ? "completion-status good"
                            : "completion-status incomplete"
                        }
                      >
                        {profileCompletion >= 85
                          ? "Excellent"
                          : profileCompletion >= 60
                          ? "Good"
                          : "Incomplete"}
                      </span>
                    </div>

                    <div className="completion-progress">
                      <span style={{ width: `${profileCompletion}%` }} />
                    </div>

                    <p>
                      Add the missing details to improve customer trust and
                      profile visibility.
                    </p>

                    {missingDetails.length > 0 && (
                      <div className="missing-details">
                        <small>Recommended next:</small>
                        <div>
                          {missingDetails.slice(0, 4).map((item) => (
                            <span key={item}>{item}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </article>
                </aside>
              </section>

              <section className="profile-information-grid">
                <div className="profile-main-column">
                  <article className="profile-card contact-card">
                    <div className="profile-section-heading">
                      <div>
                        <h2>Contact Information</h2>
                        <p>Public details customers can use for enquiries.</p>
                      </div>
                    </div>

                    <div className="contact-detail-list">
                      <ContactItem
                        icon={<FaEnvelope />}
                        label="Email"
                        value={profile.email}
                      />
                      <ContactItem
                        icon={<FaPhone />}
                        label="Phone"
                        value={profile.phone}
                      />
                      <ContactItem
                        icon={<FaEnvelope />}
                        label="Alternate Email"
                        value={profile.alternateEmail}
                      />
                      <ContactItem
                        icon={<FaPhone />}
                        label="Alternate Phone"
                        value={profile.alternatePhone}
                      />
                      <ContactItem
                        icon={<FaMapMarkerAlt />}
                        label="Location"
                        value={profile.location}
                      />
                      <ContactItem
                        icon={<FaGlobe />}
                        label="Website"
                        value={profile.website}
                      />
                    </div>
                  </article>

                  <article className="profile-card agency-card">
                    <div className="profile-section-heading">
                      <div>
                        <h2>About the Agency</h2>
                        <p>
                          Business capability and booking information for
                          customers.
                        </p>
                      </div>
                    </div>

                    <div className="agency-detail-grid">
                      <DetailItem label="Business Type" value={profile.businessType} />
                      <DetailItem label="Founded" value={profile.foundedYear} />
                      <DetailItem label="Experience" value={profile.experience} />
                      <DetailItem label="Team Size" value={profile.teamSize} />
                      <DetailItem label="Service Area" value={profile.serviceArea} />
                      <DetailItem
                        label="Travel Availability"
                        value={profile.travelAvailability}
                      />
                      <DetailItem label="Response Time" value={profile.responseTime} />
                      <DetailItem label="Working Hours" value={profile.workingHours} />
                      <DetailItem label="Languages" value={profile.languages} />
                      <DetailItem
                        label="Minimum Booking"
                        value={profile.minimumBooking}
                      />
                      <DetailItem
                        label="Advance Notice"
                        value={profile.bookingNotice}
                      />
                      <DetailItem label="GST Number" value={profile.gstNumber} />
                      <DetailItem
                        label="Registration Number"
                        value={profile.registrationNumber}
                      />
                    </div>
                  </article>

                  <article className="profile-card services-card">
                    <div className="profile-section-heading">
                      <div>
                        <h2>Services & Customer Profile</h2>
                        <p>
                          More information about what the partner offers and
                          whom they serve.
                        </p>
                      </div>
                    </div>

                    <div className="information-block-grid">
                      <InformationBlock
                        title="Specializations"
                        value={profile.specializations}
                      />
                      <InformationBlock
                        title="Event Types Served"
                        value={profile.eventTypes}
                      />
                      <InformationBlock
                        title="Target Customers"
                        value={profile.targetAudience}
                      />
                      <InformationBlock
                        title="Products & Services Summary"
                        value={profile.catalogueSummary}
                      />
                      <InformationBlock
                        title="Portfolio"
                        value={profile.portfolioLink}
                      />
                      <InformationBlock
                        title="Payment Terms"
                        value={profile.paymentTerms}
                      />
                      <InformationBlock
                        title="Cancellation Policy"
                        value={profile.cancellationPolicy}
                      />
                      <InformationBlock title="Instagram" value={profile.instagram} />
                      <InformationBlock title="Facebook" value={profile.facebook} />
                      <InformationBlock title="YouTube" value={profile.youtube} />
                      <InformationBlock title="LinkedIn" value={profile.linkedin} />
                      <InformationBlock title="Twitter / X" value={profile.twitter} />
                    </div>
                  </article>

                  <article className="profile-card products-card">
                    <div className="profile-section-heading">
                      <div>
                        <h2>Products & Services</h2>
                        <p>
                          Products and services added by this partner are managed
                          directly from the profile.
                        </p>
                      </div>

                      <button
                        type="button"
                        className="profile-text-link offering-inline-add"
                        onClick={openAddOfferingForm}
                      >
                        <FaPlus />
                        Add Offering
                      </button>
                    </div>

                    <div className="offerings-table-wrapper">
                      <table className="offerings-table">
                        <thead>
                          <tr>
                            <th>Offering</th>
                            <th>Offering Details</th>
                            <th>Price</th>
                            <th>Availability</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {offerings.length > 0 ? (
                            offerings.map((offering) => {
                              const offeringKind = getOfferingKind(offering);
                              const availability = getOfferingAvailability(offering);
                              const price = getOfferingPrice(offering);

                              return (
                                <tr key={offering.id}>
                                  <td>
                                    <div className="offering-name-cell">
                                      <span>
                                        {offering.mainImage ? (
                                          <img
                                            src={offering.mainImage}
                                            alt={offering.name}
                                            className="offering-table-image"
                                          />
                                        ) : (
                                          <FaBriefcase />
                                        )}
                                      </span>
                                      <div>
                                        <strong>{offering.name}</strong>
                                        <small>{offering.code || "No code"}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="offering-details-cell">
                                    <span
                                      className={`offering-kind-badge ${offeringKind.toLowerCase()}`}
                                    >
                                      {offeringKind}
                                    </span>
                                      <strong>{offering.category || "Uncategorised"}</strong>
                                      <small>
                                        {offering.offeringType ||
                                          offering.type ||
                                          "Type not added"}
                                      </small>
                                    </div>
                                  </td>
                                  <td>
                                    <strong>
                                      ₹{Number(price || 0).toLocaleString("en-IN")}
                                    </strong>
                                  </td>
                                  <td>
                                    <span
                                      className={`availability-tag ${String(availability)
                                        .toLowerCase()
                                        .replaceAll(" ", "-")}`}
                                    >
                                      {availability}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="offering-row-actions">
                                      <button
                                        type="button"
                                        className="view-offering-button"
                                        onClick={() => setViewingOffering(offering)}
                                        aria-label={`View ${offering.name}`}
                                      >
                                        <FaEye />
                                        <span>View</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => openEditOfferingForm(offering)}
                                        aria-label={`Edit ${offering.name}`}
                                      >
                                        <FaEdit />
                                      </button>
                                      <button
                                        type="button"
                                        className="delete"
                                        onClick={() => handleDeleteOffering(offering)}
                                        aria-label={`Delete ${offering.name}`}
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5">
                                <div className="offering-empty-state">
                                  <FaBriefcase />
                                  <strong>No products or services added yet</strong>
                                  <p>
                                    Use Add Product / Service to create the first
                                    offering.
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </article>
                </div>

                <aside className="profile-side-column">
                  <article className="profile-card quick-facts-card">
                    <div className="profile-section-heading">
                      <div>
                        <h2>Quick Facts</h2>
                        <p>Useful partner information at a glance.</p>
                      </div>
                    </div>

                    <div className="quick-facts-list">
                      <div>
                        <FaCalendarAlt />
                        <span>
                          <small>Founded</small>
                          <strong>{profile.foundedYear || "Not added"}</strong>
                        </span>
                      </div>
                      <div>
                        <FaBuilding />
                        <span>
                          <small>Business Type</small>
                          <strong>{profile.businessType || "Not added"}</strong>
                        </span>
                      </div>
                      <div>
                        <FaMapMarkerAlt />
                        <span>
                          <small>Service Coverage</small>
                          <strong>{profile.serviceArea || "Not added"}</strong>
                        </span>
                      </div>
                    </div>
                  </article>
                </aside>
              </section>

              <section className="profile-card reviews-card">
                <div className="profile-section-heading reviews-heading">
                  <div>
                    <h2>Ratings & Reviews</h2>
                    <p>Feedback received from verified customers.</p>
                  </div>

                  <div className="review-summary">
                    <strong>{averageRating}</strong>
                    <div>
                      <span className="review-stars">
                        {renderStars(Math.round(Number(averageRating)))}
                      </span>
                      <small>{reviews.length} recent reviews</small>
                    </div>
                  </div>
                </div>

                <div className="reviews-grid">
                  {reviews.length > 0 ? (
                    reviews.map((review) => (
                      <article className="review-item" key={review.id}>
                        <div className="review-item-header">
                          <div>
                            <strong>{review.customer}</strong>
                            <small>{review.date}</small>
                          </div>

                          <span className="review-stars">
                            {renderStars(review.rating)}
                          </span>
                        </div>

                        <p>{review.comment}</p>
                      </article>
                    ))
                  ) : (
                    <p>No reviews are connected to this profile yet.</p>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </main>

      {isOfferingModalOpen && (
        <div className="offering-modal-backdrop" role="presentation">
          <div
            className="offering-modal offering-view-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="offering-modal-title"
          >
            <div className="offering-modal-header">
              <div>
                <span>Partner Profile</span>
                <h2 id="offering-modal-title">
                  {editingOfferingId
                    ? "Edit Product / Service"
                    : "Add Product / Service"}
                </h2>
                <p>
                  Select whether this is a product or a service, then complete
                  the applicable details.
                </p>
              </div>

              <button
                type="button"
                className="offering-modal-close"
                onClick={closeOfferingForm}
                aria-label="Close offering form"
              >
                <FaTimes />
              </button>
            </div>

            <form className="offering-modal-form" onSubmit={handleOfferingSubmit}>
              <OfferingFormSection title="What are you adding?">
                <div className="offering-kind-selector full-width">
                  {["Service", "Product"].map((kind) => (
                    <label
                      key={kind}
                      className={`offering-kind-option ${
                        offeringForm.offeringKind === kind ? "selected" : ""
                      }`}
                    >
                      <input
                        type="radio"
                        name="offeringKind"
                        value={kind}
                        checked={offeringForm.offeringKind === kind}
                        onChange={handleOfferingFieldChange}
                      />
                      <span className="offering-kind-icon">
                        {kind === "Service" ? <FaBriefcase /> : <FaImage />}
                      </span>
                      <span>
                        <strong>{kind}</strong>
                        <small>
                          {kind === "Service"
                            ? "Performance, workshop, booking, training, consultation..."
                            : "Physical merchandise, digital product, ticket or other saleable item..."}
                        </small>
                      </span>
                    </label>
                  ))}
                </div>
              </OfferingFormSection>

              <OfferingFormSection title="Basic Details">
                <OfferingField
                  label={
                    offeringForm.offeringKind === "Product"
                      ? "Product Name"
                      : "Service Name"
                  }
                  name="name"
                  value={offeringForm.name}
                  onChange={handleOfferingFieldChange}
                  required
                />

                <OfferingSelect
                  label={
                    offeringForm.offeringKind === "Product"
                      ? "Product Type"
                      : "Service Type"
                  }
                  name="offeringType"
                  value={offeringForm.offeringType}
                  onChange={handleOfferingFieldChange}
                  options={
                    offeringForm.offeringKind === "Product"
                      ? [
                          "Physical Product",
                          "Digital Product",
                          "Merchandise",
                          "Ticket",
                          "Other Product",
                        ]
                      : [
                          "Performance",
                          "Workshop",
                          "Booking Service",
                          "Training",
                          "Consultation",
                          "Event Service",
                          "Other Service",
                        ]
                  }
                  required
                />

                <OfferingField
                  label={
                    offeringForm.offeringKind === "Product"
                      ? "SKU / Product Code"
                      : "Service Code"
                  }
                  name="code"
                  value={offeringForm.code}
                  onChange={handleOfferingFieldChange}
                  required
                />

                <OfferingField
                  label="Description"
                  name="description"
                  value={offeringForm.description}
                  onChange={handleOfferingFieldChange}
                  textarea
                  fullWidth
                  required
                />
              </OfferingFormSection>

              <OfferingFormSection title="Classification">
                <OfferingSelect
                  label="Category"
                  name="category"
                  value={offeringForm.category}
                  onChange={handleOfferingFieldChange}
                  options={offeringCategoryOptions}
                  placeholder="Select category"
                  required
                />

                <OfferingSelect
                  label="Art Sector"
                  name="artSector"
                  value={offeringForm.artSector}
                  onChange={handleOfferingFieldChange}
                  options={offeringArtSectorOptions}
                  placeholder="Select art sector"
                  required
                />

                <OfferingSelect
                  label="Language"
                  name="language"
                  value={offeringForm.language}
                  onChange={handleOfferingFieldChange}
                  options={offeringLanguageOptions}
                  placeholder="Select language"
                  required
                />

                <OfferingSelect
                  label="Region"
                  name="region"
                  value={offeringForm.region}
                  onChange={handleOfferingFieldChange}
                  options={offeringRegionOptions}
                  placeholder="Select region"
                  required
                />

                <OfferingField
                  label="Suitable Event / Use Types"
                  name="eventTypes"
                  value={offeringForm.eventTypes}
                  onChange={handleOfferingFieldChange}
                  placeholder="Corporate event, wedding, retail, festival..."
                  fullWidth
                />
              </OfferingFormSection>

              <OfferingFormSection title="Pricing & Availability">
                <OfferingField
                  label="Price"
                  name="price"
                  type="number"
                  min="0"
                  value={offeringForm.price}
                  onChange={handleOfferingFieldChange}
                  required
                />

                <OfferingField
                  label="Discount Price"
                  name="discountPrice"
                  type="number"
                  min="0"
                  value={offeringForm.discountPrice}
                  onChange={handleOfferingFieldChange}
                />

                <OfferingSelect
                  label="Unit Type"
                  name="unitType"
                  value={offeringForm.unitType}
                  onChange={handleOfferingFieldChange}
                  options={
                    offeringForm.offeringKind === "Product"
                      ? ["Piece", "Ticket", "Package", "Set", "Download"]
                      : ["Hour", "Session", "Event", "Day", "Package"]
                  }
                />

                {offeringForm.offeringKind === "Product" ? (
                  <>
                    <OfferingField
                      label="Stock Quantity"
                      name="stockQuantity"
                      type="number"
                      min="0"
                      value={offeringForm.stockQuantity}
                      onChange={handleOfferingFieldChange}
                    />

                    <OfferingField
                      label="Low-stock Limit"
                      name="lowStockLimit"
                      type="number"
                      min="0"
                      value={offeringForm.lowStockLimit}
                      onChange={handleOfferingFieldChange}
                    />
                  </>
                ) : (
                  <>
                    <OfferingField
                      label="Duration"
                      name="duration"
                      value={offeringForm.duration}
                      onChange={handleOfferingFieldChange}
                      placeholder="Example: 2 Hours"
                    />

                    <OfferingField
                      label="Number of Artists / Staff"
                      name="numberOfArtists"
                      type="number"
                      min="0"
                      value={offeringForm.numberOfArtists}
                      onChange={handleOfferingFieldChange}
                    />
                  </>
                )}
              </OfferingFormSection>

              {offeringForm.offeringKind === "Service" && (
                <OfferingFormSection title="Service Requirements">
                  <OfferingField
                    label="Technical Requirements"
                    name="technicalRequirements"
                    value={offeringForm.technicalRequirements}
                    onChange={handleOfferingFieldChange}
                    textarea
                    fullWidth
                    placeholder="Stage, power, equipment, space or customer prerequisites..."
                  />
                </OfferingFormSection>
              )}

              <OfferingFormSection title="Specifications">
                <div className="offering-specifications full-width">
                  <div className="offering-specifications-header">
                    <div>
                      <strong>Additional Specifications</strong>
                      <small>
                        Add flexible key-value details for either a product or a service.
                      </small>
                    </div>

                    <button type="button" onClick={addOfferingSpecification}>
                      <FaPlus />
                      Add Specification
                    </button>
                  </div>

                  {offeringForm.specifications.map((specification, index) => (
                    <div
                      className="offering-specification-row"
                      key={`offering-spec-${index}`}
                    >
                      <input
                        type="text"
                        placeholder="Key, e.g. Material / Genre"
                        value={specification.key}
                        onChange={(event) =>
                          handleOfferingSpecificationChange(
                            index,
                            "key",
                            event.target.value
                          )
                        }
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={specification.value}
                        onChange={(event) =>
                          handleOfferingSpecificationChange(
                            index,
                            "value",
                            event.target.value
                          )
                        }
                      />
                      <button
                        type="button"
                        onClick={() => removeOfferingSpecification(index)}
                        aria-label="Remove specification"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              </OfferingFormSection>

              <OfferingFormSection title="Media">
                <label className="offering-file-field">
                  <span>Main Image</span>
                  <div>
                    <FaImage />
                    <strong>
                      {offeringForm.mainImage
                        ? "Main image selected"
                        : "Select main image"}
                    </strong>
                    <small>JPG, PNG or WEBP. Maximum 1 MB.</small>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleOfferingMainImage}
                  />
                </label>

                <label className="offering-file-field">
                  <span>Gallery Images</span>
                  <div>
                    <FaImage />
                    <strong>
                      {offeringForm.galleryImages.length > 0
                        ? `${offeringForm.galleryImages.length} images selected`
                        : "Select up to 4 images"}
                    </strong>
                    <small>Each image must be under 1 MB.</small>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleOfferingGalleryImages}
                  />
                </label>

                <OfferingField
                  label="Video / Portfolio Link"
                  name="videoLink"
                  value={offeringForm.videoLink}
                  onChange={handleOfferingFieldChange}
                  placeholder="https://..."
                  fullWidth
                />
              </OfferingFormSection>

              <OfferingFormSection title="Fulfilment & Status">
                <OfferingSelect
                  label="Status"
                  name="status"
                  value={offeringForm.status}
                  onChange={handleOfferingFieldChange}
                  options={["Active", "Inactive", "Draft"]}
                />

                <OfferingField
                  label={
                    offeringForm.offeringKind === "Product"
                      ? "Shipping / Delivery Availability"
                      : "Travel Availability"
                  }
                  name="travelAvailability"
                  value={offeringForm.travelAvailability}
                  onChange={handleOfferingFieldChange}
                  placeholder={
                    offeringForm.offeringKind === "Product"
                      ? "Example: Shipping across India"
                      : "Example: Available across Maharashtra"
                  }
                />

                <OfferingField
                  label={
                    offeringForm.offeringKind === "Product"
                      ? "Dispatch / Preparation Time"
                      : "Booking / Preparation Time"
                  }
                  name="preparationTime"
                  value={offeringForm.preparationTime}
                  onChange={handleOfferingFieldChange}
                  placeholder="Example: 2 Days"
                />

                <OfferingField
                  label="Cancellation / Return Policy"
                  name="cancellationPolicy"
                  value={offeringForm.cancellationPolicy}
                  onChange={handleOfferingFieldChange}
                  textarea
                  fullWidth
                />
              </OfferingFormSection>

              <div className="offering-modal-footer">
                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={closeOfferingForm}
                >
                  Cancel
                </button>
                <button type="submit" className="profile-primary-button" disabled={isOfferingSaving}>
                  {isOfferingSaving
                    ? "Saving..."
                    : editingOfferingId
                    ? "Update Product / Service"
                    : `Save ${offeringForm.offeringKind}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Read-only "View" modal — shows every field + all images for one offering */}
      {viewingOffering && (
        <div className="offering-modal-backdrop" role="presentation">
          <div
            className="offering-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="view-offering-title"
          >
            <div className="offering-modal-header">
              <div>
                <span>{getOfferingKind(viewingOffering)}</span>
                <h2 id="view-offering-title">{viewingOffering.name}</h2>
                <p>{viewingOffering.code || "No code"}</p>
              </div>

              <button
                type="button"
                className="offering-modal-close"
                onClick={() => setViewingOffering(null)}
                aria-label="Close view"
              >
                <FaTimes />
              </button>
            </div>

            <div className="offering-modal-form">
              {viewingOffering.mainImage && (
                <OfferingFormSection title="Main Image">
                  <img
                    src={viewingOffering.mainImage}
                    alt={viewingOffering.name}
                    style={{ maxWidth: "240px", borderRadius: "10px" }}
                  />
                </OfferingFormSection>
              )}

              {viewingOffering.galleryImages?.length > 0 && (
                <OfferingFormSection title="Gallery">
                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    {viewingOffering.galleryImages.map((src, idx) => (
                      <img
                        key={idx}
                        src={src}
                        alt={`${viewingOffering.name} ${idx + 1}`}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "8px",
                        }}
                      />
                    ))}
                  </div>
                </OfferingFormSection>
              )}

              <OfferingFormSection title="Basic Details">
                <ViewField label="Description" value={viewingOffering.description} fullWidth />
                <ViewField label="Category" value={viewingOffering.category} />
                <ViewField label="Art Sector" value={viewingOffering.artSector} />
                <ViewField label="Language" value={viewingOffering.language} />
                <ViewField label="Region" value={viewingOffering.region} />
                <ViewField label="Event Types" value={viewingOffering.eventTypes} />
              </OfferingFormSection>

              <OfferingFormSection title="Pricing & Availability">
                <ViewField
                  label="Price"
                  value={`₹${Number(viewingOffering.price || 0).toLocaleString("en-IN")}`}
                />
                {viewingOffering.discountPrice ? (
                  <ViewField
                    label="Discount Price"
                    value={`₹${Number(viewingOffering.discountPrice).toLocaleString("en-IN")}`}
                  />
                ) : null}
                <ViewField label="Unit Type" value={viewingOffering.unitType} />
                <ViewField label="Status" value={viewingOffering.status} />
                {getOfferingKind(viewingOffering) === "Product" ? (
                  <>
                    <ViewField label="Stock Quantity" value={viewingOffering.stockQuantity} />
                    <ViewField label="Low-stock Limit" value={viewingOffering.lowStockLimit} />
                  </>
                ) : (
                  <>
                    <ViewField label="Duration" value={viewingOffering.duration} />
                    <ViewField
                      label="Number of Artists"
                      value={viewingOffering.numberOfArtists}
                    />
                  </>
                )}
              </OfferingFormSection>

              {viewingOffering.technicalRequirements && (
                <OfferingFormSection title="Technical Requirements">
                  <ViewField value={viewingOffering.technicalRequirements} fullWidth />
                </OfferingFormSection>
              )}

              {viewingOffering.specifications?.some((s) => s.key || s.value) && (
                <OfferingFormSection title="Specifications">
                  <div
                    className="full-width"
                    style={{ display: "flex", flexDirection: "column", gap: "6px" }}
                  >
                    {viewingOffering.specifications
                      .filter((s) => s.key || s.value)
                      .map((s, idx) => (
                        <div key={idx} style={{ display: "flex", gap: "8px" }}>
                          <strong>{s.key}:</strong> <span>{s.value}</span>
                        </div>
                      ))}
                  </div>
                </OfferingFormSection>
              )}

              <OfferingFormSection title="Fulfilment">
                <ViewField
                  label="Travel/Delivery Availability"
                  value={viewingOffering.travelAvailability}
                />
                <ViewField label="Preparation Time" value={viewingOffering.preparationTime} />
                <ViewField
                  label="Cancellation Policy"
                  value={viewingOffering.cancellationPolicy}
                  fullWidth
                />
                {viewingOffering.videoLink && (
                  <ViewField
                    label="Video / Portfolio Link"
                    value={
                      <a href={viewingOffering.videoLink} target="_blank" rel="noreferrer">
                        {viewingOffering.videoLink}
                      </a>
                    }
                    fullWidth
                  />
                )}
              </OfferingFormSection>

              <div className="offering-modal-footer">
                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={() => setViewingOffering(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="profile-primary-button"
                  onClick={() => {
                    openEditOfferingForm(viewingOffering);
                    setViewingOffering(null);
                  }}
                >
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isBrochureModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "24px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "760px",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "28px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <div>
                <h2 style={{ margin: 0 }}>Generate Business Brochure</h2>
                <p style={{ margin: "4px 0 0", color: "#64748b" }}>
                  Fill in your enterprise details below. A downloadable PDF
                  brochure will be generated automatically from this
                  information.
                </p>
              </div>

              <button
                type="button"
                className="profile-secondary-button"
                onClick={closeBrochureForm}
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleGenerateBrochure}>
              <div
                className="profile-edit-logo-row"
                style={{ marginBottom: "20px" }}
              >
                <div className="profile-logo large">
                  {brochureLogo ? (
                    <img src={brochureLogo} alt="Brochure logo preview" />
                  ) : (
                    <FaBuilding />
                  )}
                </div>

                <div>
                  <label className="profile-upload-button">
                    <FaUpload />
                    Upload Logo for Brochure
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBrochureLogoChange}
                    />
                  </label>
                  <small>
                    Used on the brochure cover. Defaults to your profile logo.
                  </small>
                </div>
              </div>

              <div className="profile-form-grid">
                <ProfileField
                  label="Agency / Business Name"
                  name="agencyName"
                  value={brochureDetails.agencyName}
                  onChange={handleBrochureFieldChange}
                  required
                />
                <ProfileField
                  label="Tagline"
                  name="tagline"
                  value={brochureDetails.tagline}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="About the Business"
                  name="description"
                  value={brochureDetails.description}
                  onChange={handleBrochureFieldChange}
                  textarea
                  fullWidth
                  required
                />
                <ProfileField
                  label="Business Type"
                  name="businessType"
                  value={brochureDetails.businessType}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Founded Year"
                  name="foundedYear"
                  value={brochureDetails.foundedYear}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Experience"
                  name="experience"
                  value={brochureDetails.experience}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Team Size"
                  name="teamSize"
                  value={brochureDetails.teamSize}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Service Area"
                  name="serviceArea"
                  value={brochureDetails.serviceArea}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Travel Availability"
                  name="travelAvailability"
                  value={brochureDetails.travelAvailability}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Languages"
                  name="languages"
                  value={brochureDetails.languages}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Working Hours"
                  name="workingHours"
                  value={brochureDetails.workingHours}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Specializations"
                  name="specializations"
                  value={brochureDetails.specializations}
                  onChange={handleBrochureFieldChange}
                  textarea
                  fullWidth
                />
                <ProfileField
                  label="Event Types Served"
                  name="eventTypes"
                  value={brochureDetails.eventTypes}
                  onChange={handleBrochureFieldChange}
                  textarea
                  fullWidth
                />
                <ProfileField
                  label="Target Customers"
                  name="targetAudience"
                  value={brochureDetails.targetAudience}
                  onChange={handleBrochureFieldChange}
                  textarea
                  fullWidth
                />
                <ProfileField
                  label="Products & Services Summary"
                  name="catalogueSummary"
                  value={brochureDetails.catalogueSummary}
                  onChange={handleBrochureFieldChange}
                  textarea
                  fullWidth
                />
                <ProfileField
                  label="Location"
                  name="location"
                  value={brochureDetails.location}
                  onChange={handleBrochureFieldChange}
                />
                <ProfileField
                  label="Website"
                  name="website"
                  value={brochureDetails.website}
                  onChange={handleBrochureFieldChange}
                />
              </div>

              <div style={{ marginTop: "26px" }}>
                <h3
                  style={{
                    margin: "0 0 4px",
                    color: "var(--profile-maroon)",
                    fontSize: "14px",
                    fontWeight: 800,
                  }}
                >
                  Products & Services
                </h3>
                <p style={{ margin: "0 0 14px", color: "var(--profile-grey)", fontSize: "12px" }}>
                  Add the products or services to feature in the brochure, each with a
                  photo. These print as a showcase section in the PDF.
                </p>

                {brochureProducts.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      marginBottom: "16px",
                    }}
                  >
                    {brochureProducts.map((product) => (
                      <div
                        key={product.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px",
                          border: "1px solid #e1e1e1",
                          borderRadius: "10px",
                          background: "#fafafa",
                        }}
                      >
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            flexShrink: 0,
                            borderRadius: "8px",
                            overflow: "hidden",
                            background: "var(--profile-maroon-light)",
                            display: "grid",
                            placeItems: "center",
                          }}
                        >
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <FaBriefcase style={{ color: "var(--profile-maroon)" }} />
                          )}
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <strong
                            style={{
                              display: "block",
                              fontSize: "12px",
                              color: "var(--profile-black)",
                            }}
                          >
                            {product.name}
                            {product.price ? `  •  ${product.price}` : ""}
                          </strong>
                          {product.description && (
                            <small
                              style={{
                                display: "block",
                                marginTop: "3px",
                                fontSize: "10.5px",
                                color: "var(--profile-grey)",
                              }}
                            >
                              {product.description}
                            </small>
                          )}
                        </div>

                        <button
                          type="button"
                          className="profile-secondary-button"
                          style={{ minHeight: "32px", padding: "0 10px" }}
                          onClick={() => handleRemoveBrochureProduct(product.id)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  style={{
                    padding: "16px",
                    border: "1px dashed #cccccc",
                    borderRadius: "10px",
                    background: "#fcfcfc",
                  }}
                >
                  <div className="profile-form-grid">
                    <ProfileField
                      label="Product / Service Name"
                      name="name"
                      value={productDraft.name}
                      onChange={handleProductDraftChange}
                    />
                    <ProfileField
                      label="Price (optional)"
                      name="price"
                      value={productDraft.price}
                      onChange={handleProductDraftChange}
                    />
                    <ProfileField
                      label="Short Description"
                      name="description"
                      value={productDraft.description}
                      onChange={handleProductDraftChange}
                      textarea
                      fullWidth
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginTop: "14px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div
                        style={{
                          width: "44px",
                          height: "44px",
                          borderRadius: "8px",
                          overflow: "hidden",
                          background: "var(--profile-maroon-light)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                        }}
                      >
                        {productDraft.image ? (
                          <img
                            src={productDraft.image}
                            alt="Product preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          <FaBriefcase style={{ color: "var(--profile-maroon)" }} />
                        )}
                      </div>

                      <label className="profile-upload-button">
                        <FaUpload />
                        Upload Photo
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleProductDraftImageChange}
                        />
                      </label>
                    </div>

                    <button
                      type="button"
                      className="profile-primary-button"
                      onClick={handleAddBrochureProduct}
                    >
                      Add to Brochure
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="profile-form-actions"
                style={{ marginTop: "20px", justifyContent: "flex-end" }}
              >
                <button
                  type="button"
                  className="profile-secondary-button"
                  onClick={closeBrochureForm}
                >
                  <FaTimes />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="profile-primary-button"
                  disabled={isGeneratingBrochure}
                >
                  <FaFilePdf />
                  {isGeneratingBrochure ? "Generating…" : "Generate PDF Brochure"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const OfferingFormSection = ({ title, children }) => (
  <section className="offering-form-section">
    <h3>{title}</h3>
    <div className="offering-form-grid">{children}</div>
  </section>
);

const OfferingField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  textarea = false,
  fullWidth = false,
  required = false,
  placeholder = "",
  min,
}) => (
  <label className={`offering-form-field ${fullWidth ? "full-width" : ""}`}>
    <span>
      {label}
      {required && <sup>*</sup>}
    </span>
    {textarea ? (
      <textarea
        name={name}
        value={value ?? ""}
        onChange={onChange}
        rows={3}
        required={required}
        placeholder={placeholder}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        min={min}
      />
    )}
  </label>
);

const OfferingSelect = ({
  label,
  name,
  value,
  onChange,
  options,
  placeholder,
  required = false,
}) => (
  <label className="offering-form-field">
    <span>
      {label}
      {required && <sup>*</sup>}
    </span>
    <select
      name={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option value={option} key={option}>
          {option}
        </option>
      ))}
    </select>
  </label>
);

const FormSection = ({ title, children }) => (
  <section className="form-section">
    <h3>{title}</h3>
    <div className="profile-form-grid">{children}</div>
  </section>
);

const ProfileField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  textarea = false,
  fullWidth = false,
  required = false,
}) => (
  <label className={`profile-field ${fullWidth ? "full-width" : ""}`}>
    <span>{label}</span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={3}
        required={required}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
      />
    )}
  </label>
);

const ContactItem = ({ icon, label, value }) => (
  <div className="contact-detail-item">
    <span className="contact-detail-icon">{icon}</span>
    <div>
      <small>{label}</small>
      <strong>{value || "Not added"}</strong>
    </div>
  </div>
);

const DetailItem = ({ label, value }) => (
  <div>
    <small>{label}</small>
    <strong>{value || "Not added"}</strong>
  </div>
);

const InformationBlock = ({ title, value }) => (
  <div className="information-block">
    <strong>{title}</strong>
    <p>{value || "Not added"}</p>
  </div>
);

// Read-only field renderer used inside the "View" modal.
const ViewField = ({ label, value, fullWidth = false }) => (
  <div className={`offering-form-field ${fullWidth ? "full-width" : ""}`}>
    {label && <span>{label}</span>}
    <div style={{ padding: "8px 0", color: "var(--profile-black, #171717)" }}>
      {value || value === 0 ? value : "Not added"}
    </div>
  </div>
);

export default PartnerProfile;
